import { useState, useMemo, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Dialog } from '@/components/ui/Dialog';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useDataRules, type DataRule } from './hooks/useDataRules';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface LinkedDataRule {
  id: string;
  name: string;
  field: string;
  operator: string;
}

interface DataRole {
  id: string;
  name: string;
  description: string | null;
  isFullAccess: boolean;
  filterRules: { entity: string; field: string; operator: string; values: string[] }[];
  dataRuleIds: string[];
  linkedRules?: LinkedDataRule[];
  /** Count of data rules linked to this role. Returned by the API as `linkedDataRulesCount`. */
  linkedDataRulesCount: number;
  isActive: boolean;
  assignedTeamsCount: number;
  createdAt: string;
  updatedAt: string;
}

interface CreateDataRoleData {
  name: string;
  description: string;
  isFullAccess: boolean;
  dataRuleIds: string[];
}

interface UpdateDataRoleData extends CreateDataRoleData {
  isActive: boolean;
}

// ─── Main Component ─────────────────────────────────────────────────────────────

/**
 * DataRolesTab — Simplified Data Roles CRUD with linked rules selector.
 * Requirements: REQ-5.3
 */
export function DataRolesTab() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<DataRole | null>(null);
  const [deletingRole, setDeletingRole] = useState<DataRole | null>(null);
  const [sort, setSort] = useState<SortState>({ column: 'name', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState('');

  // ─── Queries ────────────────────────────────────────────────────────────────

  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['settings', 'data-roles', { status: statusFilter, page, pageSize, sort }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (sort.direction) {
        params.set('sortBy', sort.column);
        params.set('sortDirection', sort.direction);
      }
      const response = await apiClient.get<
        ApiResponse<{
          dataRoles: DataRole[];
          meta: { page: number; pageSize: number; totalCount: number };
        }>
      >(`/data-roles?${params.toString()}`);
      const { dataRoles, meta } = response.data.data;
      return { dataRoles, total: meta.totalCount, page: meta.page, pageSize: meta.pageSize };
    },
  });

  // ─── Mutations ──────────────────────────────────────────────────────────────

  const createMutation = useMutation({
    mutationFn: async (data: CreateDataRoleData) => {
      const response = await apiClient.post<ApiResponse<DataRole>>('/data-roles', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'data-roles'] });
      setIsFormOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateDataRoleData }) => {
      const response = await apiClient.put<ApiResponse<DataRole>>(`/data-roles/${id}`, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'data-roles'] });
      setIsFormOpen(false);
      setEditingRole(null);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/data-roles/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'data-roles'] });
      setDeletingRole(null);
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiClient.put<ApiResponse<DataRole>>(`/data-roles/${id}`, {
        isActive,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'data-roles'] });
    },
  });

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleNewRole = () => {
    setEditingRole(null);
    setIsFormOpen(true);
  };

  const handleEditRole = (role: DataRole) => {
    setEditingRole(role);
    setIsFormOpen(true);
  };

  const handleSaveRole = (data: CreateDataRoleData | UpdateDataRoleData) => {
    if (editingRole) {
      updateMutation.mutate({ id: editingRole.id, data: data as UpdateDataRoleData });
    } else {
      createMutation.mutate(data as CreateDataRoleData);
    }
  };

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  // ─── Columns ────────────────────────────────────────────────────────────────

  const columns: ColumnDef<DataRole>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div>
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{row.name}</span>
            {row.description && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                {row.description}
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'fullAccess',
        header: 'Full Access',
        cell: (row) => (
          <Badge variant={row.isFullAccess ? 'info' : 'default'} size="sm">
            {row.isFullAccess ? 'Yes' : 'No'}
          </Badge>
        ),
      },
      {
        id: 'linkedRules',
        header: 'Linked Rules',
        cell: (row) => {
          const count =
            row.linkedDataRulesCount ?? row.dataRuleIds?.length ?? row.linkedRules?.length ?? 0;
          if (row.isFullAccess) {
            return <span className="text-sm text-gray-400 dark:text-gray-500 italic">N/A</span>;
          }
          return (
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {count} rule{count !== 1 ? 's' : ''}
            </span>
          );
        },
      },
      {
        id: 'assignedTeamsCount',
        header: 'Assigned Teams',
        cell: (row) => (
          <span className="text-sm text-gray-700 dark:text-gray-300">{row.assignedTeamsCount}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        cell: (row) => (
          <Badge variant={row.isActive ? 'success' : 'default'} size="sm" dot>
            {row.isActive ? 'Active' : 'Inactive'}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: (row) => (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => handleEditRole(row)}>
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => toggleStatusMutation.mutate({ id: row.id, isActive: !row.isActive })}
            >
              {row.isActive ? 'Deactivate' : 'Activate'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setDeletingRole(row)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [toggleStatusMutation],
  );

  const pagination: PaginationState | undefined = rolesData
    ? { page: rolesData.page, pageSize: rolesData.pageSize, total: rolesData.total }
    : undefined;

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Data Roles"
          description="Create and manage data roles. Data roles define which records are visible to teams by linking reusable data rules."
          action={
            <Button size="sm" onClick={handleNewRole}>
              New Data Role
            </Button>
          }
        />
        <CardContent>
          <div className="mb-4">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              placeholder="All statuses"
            />
          </div>
          <DataTable<DataRole>
            data={rolesData?.dataRoles ?? []}
            columns={columns}
            keyExtractor={(row) => row.id}
            sort={sort}
            onSortChange={handleSortChange}
            pagination={pagination}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={isLoading}
            emptyMessage="No data roles found. Create one to get started."
            aria-label="Data roles table"
          />
        </CardContent>
      </Card>

      {/* Create / Edit Data Role Modal */}
      {isFormOpen && (
        <DataRoleFormModal
          role={editingRole}
          onSave={handleSaveRole}
          onClose={() => {
            setIsFormOpen(false);
            setEditingRole(null);
          }}
          isSaving={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deletingRole && (
        <Dialog
          open
          onClose={() => setDeletingRole(null)}
          onConfirm={() => deactivateMutation.mutate(deletingRole.id)}
          title="Delete Data Role"
          description={`Are you sure you want to delete "${deletingRole.name}"? This will remove it from all team assignments.`}
          confirmLabel="Delete"
          confirmVariant="danger"
          loading={deactivateMutation.isPending}
        />
      )}
    </div>
  );
}

// ─── Data Role Form Modal ───────────────────────────────────────────────────────

interface DataRoleFormModalProps {
  role: DataRole | null;
  onSave: (data: CreateDataRoleData | UpdateDataRoleData) => void;
  onClose: () => void;
  isSaving: boolean;
}

function DataRoleFormModal({ role, onSave, onClose, isSaving }: DataRoleFormModalProps) {
  const [form, setForm] = useState({
    name: role?.name ?? '',
    description: role?.description ?? '',
    isFullAccess: role?.isFullAccess ?? false,
    isActive: role?.isActive ?? true,
  });
  const [selectedRuleIds, setSelectedRuleIds] = useState<string[]>(
    role?.dataRuleIds ?? role?.linkedRules?.map((r) => r.id) ?? [],
  );

  // Fetch data rules with module = 'data_authorization' for the linked rules selector
  const { data: rulesData, isLoading: rulesLoading } = useDataRules({
    module: 'data_authorization',
    pageSize: 200,
    status: 'active',
  });

  const availableRules = rulesData?.dataRules ?? [];

  const handleToggleRule = (ruleId: string) => {
    setSelectedRuleIds((prev) =>
      prev.includes(ruleId) ? prev.filter((id) => id !== ruleId) : [...prev, ruleId],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      isFullAccess: form.isFullAccess,
      dataRuleIds: form.isFullAccess ? [] : selectedRuleIds,
      ...(role ? { isActive: form.isActive } : {}),
    };
    onSave(payload as CreateDataRoleData | UpdateDataRoleData);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={role ? `Edit Data Role: ${role.name}` : 'New Data Role'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
          required
          placeholder="e.g., Corporate Clients Only"
        />
        <Input
          label="Description"
          value={form.description}
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
          placeholder="Brief description of what this data role grants access to"
        />

        {/* Full Access Toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="full-access"
            checked={form.isFullAccess}
            onChange={(e) => setForm((prev) => ({ ...prev, isFullAccess: e.target.checked }))}
            className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
          />
          <label htmlFor="full-access" className="text-sm text-gray-700 dark:text-gray-300">
            Full Access (no filter restrictions)
          </label>
        </div>

        {role && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="role-active"
              checked={form.isActive}
              onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
            />
            <label htmlFor="role-active" className="text-sm text-gray-700 dark:text-gray-300">
              Active
            </label>
          </div>
        )}

        {/* Linked Rules Selector */}
        {!form.isFullAccess && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Linked Data Rules
              </h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {selectedRuleIds.length} selected
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Select data rules to link with this role. Only rules assigned to the Data
              Authorization module are shown.
            </p>

            {rulesLoading ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Loading data rules...</p>
            ) : availableRules.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
                No data rules with the Data Authorization module found. Create data rules in the
                Data Rules tab first.
              </p>
            ) : (
              <div className="rounded-lg border border-border dark:border-border-dark max-h-48 overflow-y-auto">
                {availableRules.map((rule) => (
                  <LinkedRuleCheckbox
                    key={rule.id}
                    rule={rule}
                    checked={selectedRuleIds.includes(rule.id)}
                    onToggle={() => handleToggleRule(rule.id)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!form.name.trim()}>
            {role ? 'Update Data Role' : 'Create Data Role'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Linked Rule Checkbox ───────────────────────────────────────────────────────

interface LinkedRuleCheckboxProps {
  rule: DataRule;
  checked: boolean;
  onToggle: () => void;
}

function LinkedRuleCheckbox({ rule, checked, onToggle }: LinkedRuleCheckboxProps) {
  return (
    <label
      className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors border-b border-border dark:border-border-dark last:border-b-0 ${
        checked ? 'bg-brand-50 dark:bg-brand-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className="h-4 w-4 rounded border-border text-brand-600 focus:ring-brand-500"
      />
      <div className="flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{rule.name}</span>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {rule.field} {rule.operator} {rule.values.join(', ')}
        </p>
      </div>
    </label>
  );
}
